import { ApiError, handleApiError } from '@/handleApiError'
import helpers from '@/modules/message/helpers'
import api from '@/modules/user/api'
import { prepareForForm } from '@/modules/user/helpers'
import useOwnUser from '@/modules/user/useOwnUser'
import { t } from '@/plugins/i18n.ts'
import { Language } from '@/types'
import { PasswordChangeDto, PermissionPatch, SettingPatch, User } from '@/types/index'
import { defineStore } from 'pinia'

interface UserState {
  user: User | undefined
  loading: boolean
  sending: boolean
}

const initialState = (): UserState => {
  return {
    user: undefined,
    loading: false,
    sending: false
  }
}

const useUser = defineStore('user', {
  state: () => initialState(),
  actions: {
    async getUser(id: string) {
      this.loading = true

      return api
        .getUser(id)
        .then(({ data }) => {
          this.user = prepareForForm(data)
        })
        .catch(handleApiError)
        .finally(() => (this.loading = false))
    },
    async updateUser(user: User) {
      if (this.user) {
        Object.assign(this.user, user)
        useOwnUser().updateOwnUser(user)

        return api.updateUser(user).catch(handleApiError)
      }
    },
    async updateLanguage(lng: Language) {
      if (this.user) {
        this.user.alpha2language = lng
        useOwnUser().updateOwnUser(this.user)

        return api.updateUser(this.user).catch(handleApiError)
      }
    },
    async updatePermissions(id: string, dto: PermissionPatch) {
      return api
        .updateUserPermissions(id, dto)
        .then(async () => {
          return api.getUser(id).then(({ data }) => {
            this.user = data
            useOwnUser().updateOwnUser(data)
          })
        })
        .catch(handleApiError)
    },
    async updateSettings(id: string, dto: SettingPatch) {
      return api
        .updateUserSettings(id, dto)
        .then(async () => {
          return api.getUser(id).then(({ data }) => {
            this.user = data
            useOwnUser().updateOwnUser(data)
          })
        })
        .catch(handleApiError)
    },
    async updateAvatar(id: string, form: FormData) {
      return api
        .updateAvatar(id, form)
        .then(async () => {
          return api.getUser(id).then(({ data }) => {
            this.user = data
            useOwnUser().updateOwnUser(data)
          })
        })
        .catch((error: ApiError) => {
          handleApiError(
            error,
            {},
            {
              errorCode: 415,
              customMessage: t('error.unsupportedMediaType'),
              logError: false
            },
            {
              errorCode: 413,
              customMessage: t('error.fileTooLarge'),
              logError: false
            }
          )
        })
    },

    async changePassword(
      password: PasswordChangeDto
    ): Promise<'SUCCESS' | 'OLD_PASSWORD_INVALID' | 'NEW_PASSWORD_INVALID'> {
      this.sending = true

      return api
        .changePassword(password)
        .then(() => {
          helpers.reportSuccess(t('success.changePassword'))
          return 'SUCCESS' as const
        })
        .catch((error: ApiError) => {
          handleApiError(
            error,
            undefined,
            {
              errorCode: 403,
              logError: false,
              customMessage: t('error.oldPassword')
            },
            {
              errorCode: 400,
              logError: false,
              customMessage: t('error.passwordRequirements')
            }
          )
          return error.code?.valueOf() == 403
            ? ('OLD_PASSWORD_INVALID' as const)
            : ('NEW_PASSWORD_INVALID' as const)
        })
        .finally(() => (this.sending = false))
    }
  }
})

export default useUser
